import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X, Phone, MessageCircle, Mail, Globe, Building2, Star, Plus,
  Trash2, Edit3, Check, Archive, ExternalLink, Tag as TagIcon,
  ChevronDown, ChevronUp, Loader2, CalendarPlus
} from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';
import { Lead, STATUS_CONFIG, LeadStatus } from '../../types/lead';
import { Note } from '../../types/note';
import { Task } from '../../types/task';
import { Tag } from '../../types/tag';
import { Stage } from '../../types/stage';
import { getNotes, createNote, updateNote, deleteNote } from '../../lib/notesManagement';
import { getTasks, createTask, toggleTaskComplete, deleteTask } from '../../lib/tasksManagement';
import { getLeadTags, setLeadTags } from '../../lib/tagsManagement';
import { updateLead } from '../../lib/leadsManagement';
import { buildCalcomUrl } from '../../lib/calcom';
import { getMetaData, metaValue } from '../../lib/metaFields';

interface LeadDetailModalProps {
  lead: Lead;
  allTags: Tag[];
  allStages: Stage[];
  currentUserId?: string;
  onClose: () => void;
  onUpdate: (updates: Partial<Lead>) => void;
  onDelete: (leadId: string) => void;
  onArchive: (leadId: string) => void;
  onNoteCreated?: (leadId: string, content: string) => void;
}

type Section = 'notes' | 'tasks' | 'fields' | 'pipeline';

const LeadDetailModal: React.FC<LeadDetailModalProps> = ({
  lead,
  allTags,
  allStages,
  currentUserId,
  onClose,
  onUpdate,
  onDelete,
  onArchive,
  onNoteCreated,
}) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [leadTags, setLeadTagsState] = useState<Tag[]>([]);
  const [activeSection, setActiveSection] = useState<Section>('notes');
  const [newNote, setNewNote] = useState('');
  const [noteImportant, setNoteImportant] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState<string | null>(null);
  const [confirmDeleteTaskId, setConfirmDeleteTaskId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [customFieldKey, setCustomFieldKey] = useState('');
  const [customFieldValue, setCustomFieldValue] = useState('');
  const [localLead, setLocalLead] = useState(lead);
  const { isKunde, isCaller, getEffectiveUser } = useAuth();

  // Cal.com-Buchungslink mit allen Lead-Daten (nur Rolle 'caller')
  const calcomUrl = isCaller()
    ? buildCalcomUrl(localLead, getEffectiveUser()?.calcom_url)
    : null;

  const handleBookAppointment = () => {
    if (calcomUrl) window.open(calcomUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    loadData();
  }, [lead.id]);

  const loadData = async () => {
    const [notesData, tasksData, tagsData] = await Promise.all([
      getNotes(lead.id),
      getTasks({ leadId: lead.id }),
      getLeadTags(lead.id),
    ]);
    setNotes(notesData);
    setTasks(tasksData);
    setLeadTagsState(tagsData);
  };

  const handleCall = () => window.location.href = `tel:${lead.phone.replace(/\s/g, '')}`;
  const handleWhatsApp = () => {
    const clean = lead.phone.replace(/\D/g, '');
    window.open(`https://wa.me/${clean}`, '_blank');
  };
  const handleSMS = () => window.location.href = `sms:${lead.phone.replace(/\s/g, '')}`;

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    const note = await createNote(lead.id, newNote.trim(), noteImportant, currentUserId);
    if (note) {
      setNotes(prev => [note, ...prev]);
      onNoteCreated?.(lead.id, note.content);
      setNewNote('');
      setNoteImportant(false);
    }
    setSavingNote(false);
  };

  const handleToggleNoteImportant = async (note: Note) => {
    const ok = await updateNote(note.id, { is_important: !note.is_important });
    if (ok) setNotes(prev => prev.map(n => n.id === note.id ? { ...n, is_important: !n.is_important } : n));
  };

  const handleDeleteNote = async (id: string) => {
    await deleteNote(id);
    setNotes(prev => prev.filter(n => n.id !== id));
    setConfirmDeleteNoteId(null);
  };

  const handleSaveEditNote = async (id: string) => {
    if (!editNoteContent.trim()) return;
    const ok = await updateNote(id, { content: editNoteContent.trim() });
    if (ok) {
      setNotes(prev => prev.map(n => n.id === id ? { ...n, content: editNoteContent.trim() } : n));
      setEditingNote(null);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    setSavingTask(true);
    const task = await createTask({
      lead_id: lead.id,
      title: newTaskTitle.trim(),
      type: 'task',
      is_important: false,
      completed: false,
      created_by: currentUserId,
    });
    if (task) {
      setTasks(prev => [...prev, task]);
      setNewTaskTitle('');
    }
    setSavingTask(false);
  };

  const handleToggleTask = async (task: Task) => {
    await toggleTaskComplete(task.id, !task.completed);
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, completed: !t.completed } : t));
  };

  const handleDeleteTask = async (id: string) => {
    await deleteTask(id);
    setTasks(prev => prev.filter(t => t.id !== id));
    setConfirmDeleteTaskId(null);
  };

  const handleToggleTag = async (tag: Tag) => {
    const has = leadTags.some(t => t.id === tag.id);
    const newTags = has ? leadTags.filter(t => t.id !== tag.id) : [...leadTags, tag];
    setLeadTagsState(newTags);
    await setLeadTags(lead.id, newTags.map(t => t.id));
  };

  const handleStatusChange = async (status: LeadStatus) => {
    const updated = { ...localLead, status, status_changed_at: new Date().toISOString() };
    setLocalLead(updated);
    onUpdate({ status, status_changed_at: new Date().toISOString() });
  };

  const handleStageChange = async (stageId: string | null) => {
    const updated = { ...localLead, stage_id: stageId };
    setLocalLead(updated);
    onUpdate({ stage_id: stageId });
  };

  const handleAddCustomField = async () => {
    if (!customFieldKey.trim() || !customFieldValue.trim()) return;
    const fields = { ...(localLead.custom_fields || {}), [customFieldKey.trim()]: customFieldValue.trim() };
    const updated = { ...localLead, custom_fields: fields };
    setLocalLead(updated);
    onUpdate({ custom_fields: fields });
    setCustomFieldKey('');
    setCustomFieldValue('');
  };

  const handleRemoveCustomField = async (key: string) => {
    const fields = { ...(localLead.custom_fields || {}) };
    delete fields[key];
    const updated = { ...localLead, custom_fields: fields };
    setLocalLead(updated);
    onUpdate({ custom_fields: fields });
  };

  const handleDelete = () => {
    onDelete(lead.id);
    onClose();
  };

  const company = localLead.company || metaValue(localLead, 'company');

  // Details aus dem Formular. Die Schlüssel unterscheiden sich je nach Quelle
  // (Meta-Formular vs. Funnel über n8n), metaValue gleicht das aus. Bei Fläche
  // und Adresse variieren zusätzlich die Feldnamen selbst, daher mehrere Varianten.
  const strasse = metaValue(localLead, 'strasse', 'street', 'adresse', 'address');
  const plz = metaValue(localLead, 'plz', 'postleitzahl', 'zip', 'postal_code');
  const ortName = metaValue(localLead, 'ort', 'stadt', 'city');
  // PLZ gehört zum Ort und wird ihm vorangestellt, falls vorhanden.
  const ort = [plz, ortName].filter(Boolean).join(' ');

  // E-Mail und Telefon stehen normalerweise in eigenen Spalten; kommen sie
  // aus dem Funnel nur in den meta_data an, greift der Fallback.
  const email = localLead.email || metaValue(localLead, 'email', 'e_mail', 'mail');
  const phone = localLead.phone || metaValue(localLead, 'phone', 'telefon', 'telefonnummer');
  const anfrageDetails = ([
    ['Projekt', metaValue(localLead, 'projekt')],
    ['Zeitraum', metaValue(localLead, 'zeitraum')],
    ['Fläche', metaValue(localLead, 'flaeche', 'qm', 'quadratmeter', 'm2', 'groesse', 'area', 'size')],
    ['Straße', strasse],
    ['Ort', ort || undefined],
  ] as Array<[string, string | undefined]>).filter(
    (eintrag): eintrag is [string, string] => Boolean(eintrag[1])
  );

  const cfg = STATUS_CONFIG[localLead.status];
  const formatDate = (iso: string) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.98 }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="relative z-[110] bg-white w-full sm:max-w-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[95vh] sm:max-h-[90vh] rounded-t-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-zinc-900 px-5 pt-5 pb-4 flex-shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-black text-white">{localLead.name}</h2>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                    {cfg.label}
                  </span>
                </div>
                {company && <p className="text-zinc-400 text-sm mt-0.5">{company}</p>}
                {localLead.website && (
                  <a
                    href={localLead.website.startsWith('http') ? localLead.website : `https://${localLead.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-zinc-400 hover:text-white text-xs mt-1 transition-colors"
                  >
                    <Globe className="w-3 h-3" />
                    {localLead.website}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Caller: Termin direkt in Cal.com buchen, Lead-Daten vorbefüllt */}
                {isCaller() && (
                  <button
                    onClick={handleBookAppointment}
                    disabled={!calcomUrl}
                    title={calcomUrl ? 'Termin in Cal.com buchen' : 'Kein Cal.com-Link hinterlegt – bitte beim Administrator melden'}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-zinc-900 text-xs font-bold uppercase tracking-widest hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <CalendarPlus className="w-4 h-4" />
                    <span className="hidden sm:inline">Termin</span>
                  </button>
                )}
                <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Anfrage-Details aus Meta-Formular bzw. Funnel */}
            {anfrageDetails.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
                {anfrageDetails.map(([label, wert]) => (
                  <div key={label}>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-widest">{label}</p>
                    <p className="text-xs text-white font-medium">{wert}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Contact Info */}
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-zinc-400">
              {email && <span>{email}</span>}
              {email && phone && <span>·</span>}
              {phone && <span>{phone}</span>}
              {localLead.meta_data?.campaign && (
                <>
                  <span>·</span>
                  <span>📢 {localLead.meta_data.campaign}</span>
                </>
              )}
            </div>

            {/* Interaction Bar */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleCall}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold transition-colors"
              >
                <Phone className="w-4 h-4" />
                Anrufen
              </button>
              <button
                onClick={handleWhatsApp}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm font-semibold transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </button>
              <button
                onClick={handleSMS}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white text-sm font-semibold transition-colors"
              >
                <Mail className="w-4 h-4" />
                SMS
              </button>
            </div>
          </div>

          {/* Tags Row */}
          {allTags.length > 0 && (
            <div className="px-5 py-2.5 border-b border-zinc-100 flex flex-wrap gap-1.5 flex-shrink-0">
              {allTags.map(tag => {
                const active = leadTags.some(t => t.id === tag.id);
                return (
                  <button
                    key={tag.id}
                    onClick={() => handleToggleTag(tag)}
                    className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-all ${
                      active ? 'text-white border-transparent' : 'bg-white border-zinc-200 text-zinc-500 hover:border-zinc-300'
                    }`}
                    style={active ? { backgroundColor: tag.color, borderColor: tag.color } : {}}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* Section Tabs */}
          {!isKunde() && (
            <div className="flex border-b border-zinc-100 flex-shrink-0">
              {(['notes', 'tasks', 'pipeline', 'fields'] as Section[]).map(s => (
                <button
                  key={s}
                  onClick={() => setActiveSection(s)}
                  className={`flex-1 py-3 text-xs font-semibold uppercase tracking-widest transition-colors ${
                    activeSection === s
                      ? 'text-zinc-900 border-b-2 border-zinc-900'
                      : 'text-zinc-400 hover:text-zinc-600'
                  }`}
                >
                  {s === 'notes' ? 'Notizen' : s === 'tasks' ? 'Aufgaben' : s === 'pipeline' ? 'Pipeline' : 'Felder'}
                </button>
              ))}
            </div>
          )}

          {/* Section Content */}
          <div className="flex-1 overflow-y-auto thin-scroll p-5">

            {/* KUNDE: Notizen + Pipeline kombiniert */}
            {isKunde() && (
              <div className="space-y-6">
                {/* Caller: Ergebnis des Anrufs festhalten */}
                {isCaller() && (
                  <div className="space-y-4">
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Kontakt-Status</p>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <button
                          key={key}
                          onClick={() => handleStatusChange(key as LeadStatus)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            localLead.status === key
                              ? 'border-zinc-900 bg-zinc-900 text-white'
                              : `${cfg.bg} ${cfg.color} border-transparent hover:border-zinc-300`
                          }`}
                        >
                          {cfg.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notizen */}
                <div className={`space-y-4 ${isCaller() ? 'border-t border-zinc-100 pt-6' : ''}`}>
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Notizen</p>
                  <div className="border border-zinc-200 rounded-xl overflow-hidden">
                    <textarea
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      placeholder="Neue Notiz..."
                      rows={3}
                      className="w-full p-3 text-sm focus:outline-none resize-none"
                    />
                    <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-t border-zinc-100">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={noteImportant} onChange={e => setNoteImportant(e.target.checked)} className="rounded" />
                        <span className="text-xs text-zinc-600 font-medium">Wichtig</span>
                      </label>
                      <button onClick={handleAddNote} disabled={!newNote.trim() || savingNote} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 text-white text-xs font-semibold disabled:opacity-40 hover:bg-zinc-800 transition-colors">
                        {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        {savingNote ? 'Speichern...' : 'Notiz hinzufügen'}
                      </button>
                    </div>
                  </div>
                  {notes.length === 0 ? (
                    <p className="text-center text-zinc-400 text-sm py-4">Noch keine Notizen</p>
                  ) : notes.map(note => (
                    <div key={note.id} className={`border rounded-xl p-4 ${note.is_important ? 'border-amber-200 bg-amber-50' : 'border-zinc-200 bg-white'}`}>
                      <p className="text-sm text-zinc-800 whitespace-pre-wrap">{note.content}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-zinc-400">{formatDate(note.created_at)}{note.author_name ? ` · ${note.author_name}` : ''}</span>
                        <button
                          onClick={() => setConfirmDeleteNoteId(note.id)}
                          className="p-1 rounded-lg text-zinc-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                          title="Notiz löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pipeline */}
                <div className="space-y-4 border-t border-zinc-100 pt-6">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Pipeline-Stufe</p>
                  <div className="space-y-2">
                    <button onClick={() => handleStageChange(null)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${!localLead.stage_id ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}>
                      <span>Nicht in Pipeline</span>
                      {!localLead.stage_id && <Check className="w-4 h-4" />}
                    </button>
                    {allStages.map(stage => (
                      <button key={stage.id} onClick={() => handleStageChange(stage.id)} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${localLead.stage_id === stage.id ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}>
                        <span>{stage.name}</span>
                        {localLead.stage_id === stage.id && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* NORMAL: Tab-basierter Content */}
            {/* NOTES */}
            {!isKunde() && activeSection === 'notes' && (
              <div className="space-y-4">
                {/* Add Note */}
                <div className="border border-zinc-200 rounded-xl overflow-hidden">
                  <textarea
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    placeholder="Neue Notiz..."
                    rows={3}
                    className="w-full p-3 text-sm focus:outline-none resize-none"
                  />
                  <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-t border-zinc-100">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={noteImportant}
                        onChange={e => setNoteImportant(e.target.checked)}
                        className="rounded"
                      />
                      <span className="text-xs text-zinc-600 font-medium">Wichtig</span>
                    </label>
                    <button
                      onClick={handleAddNote}
                      disabled={!newNote.trim() || savingNote}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 text-white text-xs font-semibold disabled:opacity-40 hover:bg-zinc-800 transition-colors"
                    >
                      {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      {savingNote ? 'Speichern...' : 'Notiz hinzufügen'}
                    </button>
                  </div>
                </div>

                {/* Notes List */}
                {notes.length === 0 ? (
                  <p className="text-center text-zinc-400 text-sm py-6">Noch keine Notizen</p>
                ) : (
                  notes.map(note => (
                    <div
                      key={note.id}
                      className={`border rounded-xl p-4 ${note.is_important ? 'border-amber-200 bg-amber-50' : 'border-zinc-200 bg-white'}`}
                    >
                      {editingNote === note.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editNoteContent}
                            onChange={e => setEditNoteContent(e.target.value)}
                            rows={3}
                            className="w-full border border-zinc-200 rounded-lg p-2 text-sm focus:outline-none resize-none"
                            autoFocus
                          />
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setEditingNote(null)} className="px-3 py-1.5 text-xs border border-zinc-200 rounded-lg hover:bg-zinc-50">Abbrechen</button>
                            <button onClick={() => handleSaveEditNote(note.id)} className="px-3 py-1.5 text-xs bg-zinc-900 text-white rounded-lg hover:bg-zinc-800">Speichern</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-zinc-800 whitespace-pre-wrap">{note.content}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-zinc-400">{formatDate(note.created_at)}{note.author_name ? ` · ${note.author_name}` : ''}</span>
                            <div className="flex gap-1">
                              <button onClick={() => handleToggleNoteImportant(note)} className={`p-1.5 rounded-lg transition-colors ${note.is_important ? 'text-amber-500 hover:bg-amber-100' : 'text-zinc-400 hover:bg-zinc-100'}`}>
                                <Star className="w-3.5 h-3.5" fill={note.is_important ? 'currentColor' : 'none'} />
                              </button>
                              <button onClick={() => { setEditingNote(note.id); setEditNoteContent(note.content); }} className="p-1.5 rounded-lg text-zinc-400 hover:bg-zinc-100 transition-colors">
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => setConfirmDeleteNoteId(note.id)} className="p-1.5 rounded-lg text-zinc-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* TASKS */}
            {!isKunde() && activeSection === 'tasks' && (
              <div className="space-y-4">
                {/* Add Task */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTaskTitle}
                    onChange={e => setNewTaskTitle(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddTask()}
                    placeholder="Neue Aufgabe..."
                    className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                  <button
                    onClick={handleAddTask}
                    disabled={!newTaskTitle.trim() || savingTask}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-semibold disabled:opacity-40 hover:bg-zinc-800 transition-colors"
                  >
                    {savingTask ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </button>
                </div>

                {tasks.length === 0 ? (
                  <p className="text-center text-zinc-400 text-sm py-6">Keine Aufgaben</p>
                ) : (
                  <div className="space-y-2">
                    {tasks.map(task => (
                      <div key={task.id} className={`flex items-center gap-3 p-3 border rounded-xl ${task.completed ? 'bg-zinc-50 border-zinc-100' : 'bg-white border-zinc-200'}`}>
                        <button
                          onClick={() => handleToggleTask(task)}
                          className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${task.completed ? 'bg-zinc-900 border-zinc-900' : 'border-zinc-300 hover:border-zinc-900'}`}
                        >
                          {task.completed && <Check className="w-3 h-3 text-white" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${task.completed ? 'line-through text-zinc-400' : 'text-zinc-900'}`}>{task.title}</p>
                          {task.due_date && (
                            <p className="text-xs text-zinc-400 mt-0.5">{formatDate(task.due_date)}</p>
                          )}
                        </div>
                        {task.is_important && <Star className="w-4 h-4 text-amber-400 flex-shrink-0" fill="currentColor" />}
                        <button onClick={() => setConfirmDeleteTaskId(task.id)} className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* PIPELINE */}
            {!isKunde() && activeSection === 'pipeline' && (
              <div className="space-y-5">
                {/* Status */}
                <div>
                  <label className="text-xs font-semibold text-zinc-500 uppercase tracking-widest block mb-2">Kontakt-Status</label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                      <button
                        key={key}
                        onClick={() => handleStatusChange(key as LeadStatus)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                          localLead.status === key
                            ? 'border-zinc-900 bg-zinc-900 text-white'
                            : `${cfg.bg} ${cfg.color} border-transparent hover:border-zinc-300`
                        }`}
                      >
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Pipeline Stage */}
                <div>
                  <label className="text-xs font-semibold text-zinc-500 uppercase tracking-widest block mb-2">Pipeline-Stufe</label>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleStageChange(null)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                        !localLead.stage_id ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                      }`}
                    >
                      <span>Nicht in Pipeline</span>
                      {!localLead.stage_id && <Check className="w-4 h-4" />}
                    </button>
                    {allStages.map(stage => (
                      <button
                        key={stage.id}
                        onClick={() => handleStageChange(stage.id)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                          localLead.stage_id === stage.id
                            ? 'border-zinc-900 bg-zinc-900 text-white'
                            : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'
                        }`}
                      >
                        <span>{stage.name}</span>
                        {localLead.stage_id === stage.id && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* CUSTOM FIELDS */}
            {!isKunde() && activeSection === 'fields' && (
              <div className="space-y-4">
                {/* Existing fields */}
                {Object.entries(localLead.custom_fields || {}).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-3 p-3 border border-zinc-200 rounded-xl bg-white">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">{key}</p>
                      <p className="text-sm text-zinc-900 mt-0.5">{val}</p>
                    </div>
                    <button onClick={() => handleRemoveCustomField(key)} className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {/* Add field */}
                <div className="border border-zinc-200 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">Neues Feld</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customFieldKey}
                      onChange={e => setCustomFieldKey(e.target.value)}
                      placeholder="Feldname (z.B. Budget)"
                      className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                    />
                    <input
                      type="text"
                      value={customFieldValue}
                      onChange={e => setCustomFieldValue(e.target.value)}
                      placeholder="Wert"
                      className="flex-1 border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                    />
                  </div>
                  <button
                    onClick={handleAddCustomField}
                    disabled={!customFieldKey.trim() || !customFieldValue.trim()}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-semibold disabled:opacity-40 hover:bg-zinc-800 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Feld hinzufügen
                  </button>
                </div>

                {/* Meta Data (read-only) */}
                {Object.keys(getMetaData(localLead)).length > 0 && (
                  <div className="border border-zinc-100 rounded-xl p-4 bg-zinc-50">
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-3">Anfrage-Daten (Facebook)</p>
                    {Object.entries(getMetaData(localLead)).map(([key, val]) => (
                      <div key={key} className="flex justify-between py-1.5 border-b border-zinc-100 last:border-0">
                        <span className="text-xs text-zinc-500 capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="text-xs font-medium text-zinc-800">{String(val)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-100 bg-white flex-shrink-0">
            <div className="flex gap-2">
              <button
                onClick={() => onArchive(lead.id)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors font-medium"
              >
                <Archive className="w-4 h-4" />
                Archivieren
              </button>
            </div>
            {!isKunde() && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors font-medium"
              >
                <Trash2 className="w-4 h-4" />
                Löschen
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Anfrage löschen?"
        message={`"${lead.name}" wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <ConfirmDialog
        open={confirmDeleteNoteId !== null}
        title="Notiz löschen?"
        message="Diese Notiz wird unwiderruflich gelöscht."
        onConfirm={() => confirmDeleteNoteId && handleDeleteNote(confirmDeleteNoteId)}
        onCancel={() => setConfirmDeleteNoteId(null)}
      />

      <ConfirmDialog
        open={confirmDeleteTaskId !== null}
        title="Aufgabe löschen?"
        message="Diese Aufgabe wird unwiderruflich gelöscht."
        onConfirm={() => confirmDeleteTaskId && handleDeleteTask(confirmDeleteTaskId)}
        onCancel={() => setConfirmDeleteTaskId(null)}
      />
    </AnimatePresence>
  );
};

export default LeadDetailModal;
